const featuredQuote = {
  quote:
    "They're the only advisors we've hired who've actually sat in the CIO seat. That's the difference between a slide deck and a runbook.",
  role: "CIO",
  client: "500-bed academic medical center",
  engagement: "EMR recovery + managed services rationalization",
};

const aggregateStats = [
  { value: "$27M+", label: "Combined annual savings" },
  { value: "15", label: "Hospitals running on our playbook" },
  { value: "7 days", label: "Fastest ransomware recovery" },
];

const cases = [
  {
    metric: "$15M",
    metricLabel: "First-year cost savings",
    client: "400-bed Southern California health system",
    story:
      "Advisory, Office 365 rollout, EMR optimization, cloud migration, and security remediation delivered as a single 12-month program.",
    tags: ["Advisory", "Healthcare IT", "Cloud", "Cyber"],
  },
  {
    metric: "$10M",
    metricLabel: "First-year savings",
    client: "500-bed Philadelphia academic medical center",
    story:
      "Recovery of a stalled EMR program plus managed services rationalization — stabilized operations and cut run-rate spend in parallel.",
    tags: ["Healthcare IT", "Advisory"],
  },
  {
    metric: "7 days",
    metricLabel: "Ransomware recovery",
    client: "Multi-state hospital network",
    story:
      "Full clinical and administrative operations restored inside a week after a catastrophic ransomware event, with a hardened control plane on exit.",
    tags: ["Cyber", "Healthcare IT"],
  },
  {
    metric: "41%",
    metricLabel: "Workflow efficiency lift",
    client: "Regional health system, 8 hospitals",
    story:
      "Revenue cycle automation across 8 departments — automating prior auth, claims triage, and documentation handoffs with governance baked in.",
    tags: ["AI Transformation", "Healthcare IT"],
  },
  {
    metric: "21%",
    metricLabel: "Capex reduction",
    client: "200+ bed Texas hospital",
    story:
      "Post-acquisition IT integration and platform consolidation — cut duplicated tooling and renegotiated vendor contracts in under 120 days.",
    tags: ["Advisory", "Cloud"],
  },
  {
    metric: "20+ yrs",
    metricLabel: "PACS archive migrated",
    client: "Multi-site imaging network",
    story:
      "Two decades of imaging archives migrated to Azure with zero clinical downtime and a new DR posture tested end-to-end.",
    tags: ["Cloud", "Healthcare IT"],
  },
];

const tagTone = {
  "AI Transformation": "text-complement-700 bg-complement-50 ring-complement-200",
  "Healthcare IT": "text-shark-700 bg-shark-100 ring-shark-200",
  Cyber: "text-rose-700 bg-rose-50 ring-rose-200",
  Cloud: "text-sky-700 bg-sky-50 ring-sky-200",
  Advisory: "text-amber-700 bg-amber-50 ring-amber-200",
};

export default function ClientOutcomes() {
  return (
    <section
      id="client-outcomes"
      className="relative overflow-hidden bg-shark-950 px-4 py-16 text-white sm:py-20 lg:px-8 lg:py-24"
    >
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_0%,rgba(231,109,56,0.12),transparent_60%)]"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -left-[10%] top-[30%] h-[320px] w-[320px] rounded-full bg-complement-500/10 blur-[120px]"
        aria-hidden
      />

      <div className="relative z-10 mx-auto max-w-6xl">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16">
          {/* ── Left column: narrative & aggregate ──────────────────── */}
          <div className="lg:sticky lg:top-[140px] lg:self-start">
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-burnt-sienna-400">
              Client outcomes
            </p>
            <h2 className="mt-3 text-[clamp(1.75rem,3.2vw,2.4rem)] font-extrabold leading-[1.1] tracking-tight text-white">
              Real engagements. Real numbers.
            </h2>
            <p className="mt-5 text-[1rem] leading-relaxed text-white/65">
              A selection of recent programs — condensed to the numbers that
              matter. Every one of these was delivered by operators who owned
              outcomes end-to-end.
            </p>

            <div className="mt-8 space-y-5 border-t border-white/10 pt-6">
              {aggregateStats.map((s) => (
                <div
                  key={s.label}
                  className="flex flex-col items-start gap-1 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4"
                >
                  <span className="text-[1.5rem] font-extrabold tracking-tight text-white">
                    {s.value}
                  </span>
                  <span className="text-left text-[11.5px] font-semibold uppercase tracking-[0.14em] text-white/55 sm:text-right">
                    {s.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* ── Right column: featured quote + case list ────────────── */}
          <div>
            <figure className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-sm sm:p-8">
              <span
                className="pointer-events-none absolute -left-2 -top-6 select-none text-[8rem] font-serif leading-none text-complement-500/20 sm:text-[9rem]"
                aria-hidden
              >
                &ldquo;
              </span>
              <span
                className="pointer-events-none absolute inset-y-0 left-0 w-[3px] bg-gradient-to-b from-burnt-sienna-400 via-burnt-sienna-500 to-complement-600"
                aria-hidden
              />
              <blockquote className="relative">
                <p className="text-[1.1rem] font-medium leading-[1.55] text-white sm:text-[1.2rem]">
                  {featuredQuote.quote}
                </p>
              </blockquote>
              <figcaption className="relative mt-5 flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="text-[12px] font-bold uppercase tracking-[0.16em] text-burnt-sienna-400">
                  {featuredQuote.role}
                </span>
                <span
                  className="h-1 w-1 rounded-full bg-white/30"
                  aria-hidden
                />
                <span className="text-[12px] font-semibold text-white/75">
                  {featuredQuote.client}
                </span>
                <span className="w-full text-[11px] font-medium text-white/40 sm:w-auto sm:before:mx-2 sm:before:content-['·']">
                  {featuredQuote.engagement}
                </span>
              </figcaption>
            </figure>

            <p className="mt-8 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/35">
              Recent engagements
            </p>

            <ol className="mt-4 space-y-6 sm:space-y-5">
              {cases.map((c, i) => (
              <li
                key={c.client}
                className="group relative grid grid-cols-1 gap-4 border-b border-white/10 pb-6 last:border-b-0 last:pb-0 min-[420px]:grid-cols-[140px_1fr] min-[420px]:gap-5 sm:grid-cols-[160px_1fr] sm:gap-8"
              >
                <div className="pt-1">
                  <p className="text-[1.65rem] font-extrabold leading-none tracking-tight text-white min-[420px]:text-[1.75rem] sm:text-[2.15rem]">
                    {c.metric}
                  </p>
                  <p className="mt-1.5 text-[10.5px] font-bold uppercase leading-tight tracking-[0.12em] text-burnt-sienna-400">
                    {c.metricLabel}
                  </p>
                </div>

                <div className="min-w-0">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/40">
                    Engagement {String(i + 1).padStart(2, "0")}
                  </p>
                  <p className="mt-1 text-[1rem] font-bold text-white">
                    {c.client}
                  </p>
                  <p className="mt-2 max-w-xl text-[0.92rem] leading-relaxed text-white/65">
                    {c.story}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {c.tags.map((t) => (
                      <span
                        key={t}
                        className={`rounded-full px-2.5 py-0.5 text-[10.5px] font-bold uppercase tracking-[0.1em] ring-1 ${
                          tagTone[t] ||
                          "text-white/70 bg-white/5 ring-white/15"
                        }`}
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              </li>
            ))}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
}
